import { APP_BASE_HREF } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { OktaAuth, OktaAuthCoreInterface, OktaAuthOptions } from '@okta/okta-auth-js';
import { OktaSignIn, WidgetOptions } from '@okta/okta-signin-widget';
import { SupportedLanguageCodes } from '@ph-static-data/languages';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { ConfigService } from '../config/config.service';
import { LoggerContext } from '../../enums/logger-context.enum';
import { LoggerService } from '../logger/logger.service';
import {
  clearIdpSessionPreexisted,
  isBoundIdpSessionStillCurrent,
  LiveIdpSession,
  readBoundOktaSession,
  writeBoundOktaSession,
} from '../auth/auth-sso-defer';
import { detectIncognito } from 'detectincognitojs';

const MOCK_ACCESS_TOKEN = 'mocked-okta-access-token';

function createMockOktaAuth(): OktaAuthCoreInterface {
  return {
    getAccessToken: () => MOCK_ACCESS_TOKEN,
    getRefreshToken: () => undefined,
    isAuthenticated: () => Promise.resolve(true),
    authStateManager: {
      subscribe: () => ({ unsubscribe: () => { } }),
      getAuthState: () => ({
        accessToken: { claims: { uid: 'mock-uid' }, accessToken: MOCK_ACCESS_TOKEN },
        refreshToken: { refreshToken: '' },
        idToken: null,
      }),
    },
  } as unknown as OktaAuthCoreInterface;
}

@Injectable({
  providedIn: 'root',
})
export class OktaService {
  private basehref = inject(APP_BASE_HREF);
  private configService = inject(ConfigService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private _oktaWidget: OktaSignIn | undefined;
  private _oktaAuth: OktaAuthCoreInterface | undefined;
  private readonly _oktaWidget$ = new BehaviorSubject<OktaSignIn | undefined>(undefined);
  private readonly _mockOktaAuth: OktaAuthCoreInterface | undefined;
  private environment = this.configService.environment;
  private readonly _useMockedOkta = this.environment.apiMock.enabled && !this.environment.auth.sso;
  private _authConfig: Partial<OktaAuthOptions> | undefined;
  private _isIncognito?: Promise<boolean>;

  constructor() {
    if (this._useMockedOkta) {
      this._mockOktaAuth = createMockOktaAuth();
      return;
    }
    if (this.environment.auth.sso) {
      const redirectUri = this.getRedirectUri();

      this._authConfig = {
        issuer: this.environment.okta.config.SSOCredentials.issuer,
        clientId: this.environment.okta.config.SSOCredentials.clientId,
        redirectUri,
        scopes: ['openid', 'email', 'profile', 'groups', 'externalDealer', 'offline_access'],
        tokenManager: { autoRenew: true },
        services: { autoRenew: true },
      };

      // Initial standalone client used to bootstrap widget creation and callbacks.
      const standaloneAuthClient = new OktaAuth(this._authConfig as OktaAuthOptions) as unknown as OktaAuthCoreInterface;
      this._oktaAuth = standaloneAuthClient;

      void this._initializeWidgetAfterActiveLanguage(standaloneAuthClient);
    }
  }

  getOktaAuth(): OktaAuthCoreInterface | undefined {
    if (this._useMockedOkta) {
      return this._mockOktaAuth;
    }
    return this._oktaAuth;
  }

  getOktaWidget(): OktaSignIn | undefined {
    if (this._useMockedOkta) {
      return undefined;
    }
    return this._oktaWidget;
  }

  getOktaWidgetStream(): Observable<OktaSignIn | undefined> {
    return this._oktaWidget$.asObservable();
  }

  /** Clears stale IDX state and re-emits widget so login can mount after callback deferral. */
  prepareForLoginWidget(): void {
    try {
      this._oktaAuth?.transactionManager?.clear?.();
      this._oktaWidget?.remove?.();
    } catch {
      /* ignore */
    }

    if (this._oktaWidget) {
      this._oktaWidget$.next(this._oktaWidget);
    }
  }

  static buildRedirectUri(origin: string, application: string, applicationInRedirectUrls: boolean): string {
    if (applicationInRedirectUrls) {
      return `${origin}/${application}/callback`;
    }
    return `${origin}/callback`;
  }

  getRedirectUri(origin: string = window.location.origin): string {
    return OktaService.buildRedirectUri(origin, this.environment.application, this.environment.okta.applicationInRedirectUrls);
  }

  clearLocalOktaTokens(): void {
    if (this._useMockedOkta) {
      return;
    }

    try {
      this._oktaAuth?.tokenManager?.clear?.();
    } catch {
      /* ignore */
    }
    clearIdpSessionPreexisted();
  }

  /**
   * True when an Okta IdP session cookie exists (may be from another portal).
   * Prefer session.get over local tokens — FCP can have no tokens while Lithia holds Okta.
   */
  async hasIdpSession(): Promise<boolean> {
    // Incognito blocks the Okta cookie, so /sessions/me 404s. Skip it; a normal window still calls.
    if (await this.isIncognitoBrowser()) {
      return false;
    }

    return typeof (await this.getLiveIdpSession()) === 'object';
  }

  /**
   * Persist this tab's Okta session id at login/home. Custom AS id tokens often omit `sid`.
   * Does not overwrite — first capture wins until logout/eject.
   */
  async captureBoundIdpSession(): Promise<void> {
    if (this._useMockedOkta) {
      return;
    }

    const application = this.environment.application;
    if (readBoundOktaSession(application)) {
      return;
    }

    const live = await this.getLiveIdpSession();
    if (typeof live !== 'object') {
      return;
    }

    writeBoundOktaSession(application, live);
  }

  isIncognitoBrowser(): Promise<boolean> {
    this._isIncognito ??= detectIncognito()
      .then((result) => result.isPrivate)
      .catch(() => false)

    return this._isIncognito;
  }

  /**
   * Logged-in tabs: live IdP session must still be this origin's user and (if captured) session id.
   */
  async isBoundIdpSessionCurrent(): Promise<boolean> {
    if (this._useMockedOkta) {
      return (await this._mockOktaAuth?.isAuthenticated()) ?? false;
    }

    const live = await this.getLiveIdpSession();
    if (live === 'unknown') {
      return true;
    }

    const bound = readBoundOktaSession(this.environment.application);
    const idTokenSub = await this.getIdTokenSubject();
    const current = isBoundIdpSessionStillCurrent(live === 'gone' ? null : live, bound, idTokenSub);
    if (current && !bound && live !== 'gone') {
      // Login-time capture can miss (3P cookie hidden then, transient error); bind once it's visible.
      writeBoundOktaSession(this.environment.application, live);
    }

    return current;
  }

  /**
   * GET /api/v1/sessions/me with the real HTTP status. okta-auth-js session.get() maps every failure
   * (429, 5xx, network) to INACTIVE, which would eject tabs on Okta hiccups.
   * 'gone' = 404 / not ACTIVE; 'unknown' = anything else, callers must not treat it as logout.
   */
  private async getLiveIdpSession(): Promise<LiveIdpSession | 'gone' | 'unknown'> {
    if (this._useMockedOkta) {
      const authenticated = (await this._mockOktaAuth?.isAuthenticated()) ?? false;

      return authenticated ? { id: 'mock-sid', userId: 'mock-user' } : 'gone';
    }

    const issuer = this.environment.okta.config.SSOCredentials.issuer;
    if (!issuer) {
      return 'unknown';
    }

    try {
      const response = await fetch(`${new URL(issuer).origin}/api/v1/sessions/me`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (response.status === 404) { //es posible diferenciar en el 404 porque fue hecho? o sea luego de login exitoso me manda 404, no deberia ser porque la sesion okta se termino 
        return 'gone';
      }
      if (!response.ok) {
        return 'unknown';
      }

      const session = (await response.json()) as { id?: unknown; userId?: unknown; status?: unknown };
      if (session.status !== 'ACTIVE' || typeof session.id !== 'string' || !session.id) {
        return 'gone';
      }

      const live: LiveIdpSession = { id: session.id };
      if (typeof session.userId === 'string' && session.userId) {
        live.userId = session.userId;
      }
      
      return live;
    } catch {
      return 'unknown';
    }
  }

  private async getIdTokenSubject(): Promise<string | null> {
    const oktaAuth = this._oktaAuth as
      | {
        authStateManager?: { getAuthState?: () => { idToken?: { claims?: { sub?: string } } } | undefined };
        tokenManager?: { get?: (key: string) => Promise<{ claims?: { sub?: string } } | undefined> };
      }
      | undefined;

    const fromState = oktaAuth?.authStateManager?.getAuthState?.()?.idToken?.claims?.sub;
    if (typeof fromState === 'string' && fromState) {
      return fromState;
    }

    try {
      const idToken = await oktaAuth?.tokenManager?.get?.('idToken');
      const sub = idToken?.claims?.sub;
      return typeof sub === 'string' && sub ? sub : null;
    } catch {
      return null;
    }
  }

  async oktaSignOut(options?: { postLogoutRedirectUri?: string }): Promise<void | boolean> {
    if (this._useMockedOkta) {
      console.info('mocked oktaSignOut triggered');
      return true;
    }

    const postLogoutRedirectUri =
      options?.postLogoutRedirectUri ??
      (this.basehref && this.environment.okta.applicationInRedirectUrls
        ? window.location.origin + '/' + this.environment.application
        : window.location.origin);
    this.logger.log(LoggerContext.Auth, 'oktaSignOut invoked', { postLogoutRedirectUri });

    // ponytail: tokens must remain until signOut completes so Okta receives id_token_hint for SLO
    return await this._oktaAuth?.signOut({
      postLogoutRedirectUri,
      clearTokensBeforeRedirect: true,
      revokeAccessToken: true,
      revokeRefreshToken: true,
    });
  }

  async refreshAccessToken(): Promise<string | undefined> {
    if (this._useMockedOkta) {
      return MOCK_ACCESS_TOKEN;
    }
    if (!this._oktaAuth) {
      return Promise.reject();
    }
    this.configService.isManualOktaTokenRefreshing.set(true);
    try {
      await this._oktaAuth.tokenManager.renew('accessToken');

      return this._oktaAuth.getAccessToken();
    } finally {
      this.configService.isManualOktaTokenRefreshing.set(false);
    }
  }

  private async _initializeWidgetAfterActiveLanguage(standaloneAuthClient: OktaAuthCoreInterface): Promise<void> {
    const preferredLanguage = this._resolvePreferredLanguage();

    if (this.translate.currentLang !== preferredLanguage) {
      try {
        await firstValueFrom(this.translate.use(preferredLanguage));
      } catch {
        // Keep widget bootstrapping resilient even if a translation file fails.
      }
    }

    this._rebuildOktaWidget(this.translate.currentLang || preferredLanguage, standaloneAuthClient);

    this.translate.onLangChange
      .pipe(
        map((event: LangChangeEvent) => event.lang),
        filter((language): language is string => !!language),
        distinctUntilChanged()
      )
      .subscribe((language) => {
        this._rebuildOktaWidget(language, standaloneAuthClient);
      });
  }

  private _rebuildOktaWidget(
    language: string | null | undefined,
    standaloneAuthClient: OktaAuthCoreInterface | undefined
  ): void {
    const normalizedLanguage = this._toSupportedLanguageCode(language);
    const idps = this.environment.okta.config.SSOCredentials.idps;
    const widgetLanguage = normalizedLanguage === SupportedLanguageCodes.French ? 'fr' : 'en';

    const widgetConfig: WidgetOptions = {
      features: { multiOptionalFactorEnroll: true },
      redirectUri: this.getRedirectUri(),
      i18n: {
        en: {
          'password.forgot.email.or.username.placeholder': 'Username',
          'errors.E0000004': 'Invalid username or password. Please ensure you are logging into this portal with your registered credentials.',
        },
        fr: {
          'password.forgot.email.or.username.placeholder': "Nom d'utilisateur",
          'errors.E0000004': "Nom d'utilisateur ou mot de passe invalide. Veuillez vous assurer de vous connecter à ce portail avec vos informations d'identification enregistrées.",
          'password.complexity.requirements': 'Exigences du mot de passe {0}.',
          'password.complexity.requirements.header': 'Exigences du mot de passe:',
        },
      },
      language: widgetLanguage,
    };

    if (!idps.length) {
      widgetConfig.issuer = this._authConfig?.issuer;
      widgetConfig.clientId = this._authConfig?.clientId;
      widgetConfig.scopes = this._authConfig?.scopes;
    }

    if (standaloneAuthClient) {
      widgetConfig.authClient = standaloneAuthClient as unknown as WidgetOptions['authClient'];
      if (idps.length) {
        widgetConfig.idps = idps.map(({ idpId: id, buttonLabelTranslationKey }) => ({
          id,
          text: this.translate.instant(buttonLabelTranslationKey),
          className: 'idp-login',
        }));
      }
    }

    this._oktaWidget = new OktaSignIn(widgetConfig);
    // Widget rebuild can return a new auth client instance; this intentionally becomes the active one.
    const authClientFromWidget = this._oktaWidget.authClient as OktaAuthCoreInterface;
    this._oktaAuth = authClientFromWidget;
    this._oktaWidget$.next(this._oktaWidget);

    if (idps.length) {
      this._oktaWidget.on('afterRender', () => {
        document.querySelector('.auth-header')?.remove();
        document.querySelector('.auth-divider')?.remove();
        document.querySelector('.o-form-fieldset-container')?.remove();
        document.querySelector('.okta-form-title')?.remove();
        document.querySelector('.o-form-button-bar')?.remove();
        document.querySelector('.auth-footer')?.remove();
      });
    }
  }

  private _resolvePreferredLanguage(): SupportedLanguageCodes {
    const storedLanguage = localStorage.getItem('languageCode') as SupportedLanguageCodes | null;
    if (storedLanguage) {
      return this._toSupportedLanguageCode(storedLanguage);
    }

    const supportsFrench = this.environment.features.supportMultiLanguage;
    return navigator.language?.startsWith('fr') && supportsFrench
      ? SupportedLanguageCodes.French
      : SupportedLanguageCodes.EnglishCA;
  }

  private _toSupportedLanguageCode(language: string | null | undefined): SupportedLanguageCodes {
    if (language === SupportedLanguageCodes.French || language?.startsWith('fr')) {
      return this.environment.features.supportMultiLanguage ? SupportedLanguageCodes.French : SupportedLanguageCodes.EnglishCA;
    }
    return SupportedLanguageCodes.EnglishCA;
  }
}
